import React, { useState } from "react";
import dataAvis from "../lib/data/dataAvis";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { formatDateLong } from "../lib/utils/dateUtils";

const Recommandations = () => {
  // Utilisation des données locales
  const recommandations = dataAvis;
  const loading = false;
  const error = null;
  const [currentIndex, setCurrentIndex] = useState(0);

  // Nombre de témoignages visibles simultanément selon la taille d'écran
  const getVisibleItems = () => {
    if (typeof globalThis !== "undefined" && globalThis.innerWidth) {
      if (globalThis.innerWidth >= 768) return 2; // md et plus (tablette/desktop)
      return 1; // mobile
    }
    return 1; // fallback pour SSR - sera mis à jour côté client
  };

  const [visibleItems, setVisibleItems] = useState(1); // État initial côté serveur

  // Mise à jour initiale côté client
  React.useEffect(() => {
    setVisibleItems(getVisibleItems());
  }, []);

  // Mise à jour du nombre d'éléments lors du redimensionnement
  React.useEffect(() => {
    const handleResize = () => setVisibleItems(getVisibleItems());
    if (typeof globalThis !== "undefined" && globalThis.addEventListener) {
      globalThis.addEventListener("resize", handleResize);
      return () => globalThis.removeEventListener("resize", handleResize);
    }
  }, []);

  // Reset index si on change la taille d'écran
  React.useEffect(() => {
    const maxPossibleIndex =
      Math.ceil(recommandations.length / visibleItems) - 1;
    if (currentIndex > maxPossibleIndex) {
      setCurrentIndex(Math.max(0, maxPossibleIndex));
    }
  }, [visibleItems, recommandations.length, currentIndex]);

  const totalPages = Math.ceil(recommandations.length / visibleItems);
  const maxIndex = Math.max(0, totalPages - 1);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex >= maxIndex ? 0 : prevIndex + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex <= 0 ? maxIndex : prevIndex - 1));
  };

  // Fonction pour formater la date - utilise l'utilitaire centralisé
  // (supprimé car remplacé par import)

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="mx-10 px-4">
          <h2 className="text-3xl text-bleuciel text-center mb-5">
            Témoignages clients
          </h2>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center text-red-600">
            <p>Erreur lors du chargement des témoignages : {error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!recommandations?.length) {
    return null;
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center">
          <h2 className="text-3xl font-semibold text-center mb-5">
            Témoignages clients
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            Découvrez ce que mes clients pensent de mes services et de mon
            expertise.
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative max-w-7xl mx-auto">
          {/* Navigation gauche */}
          <button
            onClick={prevSlide}
            disabled={recommandations.length <= visibleItems}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-shadow duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Témoignage précédent">
            <FontAwesomeIcon
              icon={faChevronLeft}
              className="text-gray-600 w-5 h-5"
            />
          </button>

          {/* Navigation droite */}
          <button
            onClick={nextSlide}
            disabled={recommandations.length <= visibleItems}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-shadow duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Témoignage suivant">
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-gray-600 w-5 h-5"
            />
          </button>

          {/* Container des témoignages */}
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / totalPages)}%)`,
                width: `${totalPages * 100}%`,
              }}>
              {Array.from({ length: totalPages }, (_, pageIndex) => (
                <div
                  key={pageIndex}
                  className="flex flex-shrink-0"
                  style={{ width: `${100 / totalPages}%` }}>
                  {recommandations
                    .slice(
                      pageIndex * visibleItems,
                      (pageIndex + 1) * visibleItems
                    )
                    .map((recommandation, itemIndex) => (
                      <div
                        key={recommandation.id}
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col flex-shrink-0"
                        style={{
                          width: `${100 / visibleItems}%`,
                          marginRight:
                            itemIndex < visibleItems - 1 && visibleItems > 1
                              ? "1rem"
                              : "0",
                        }}>
                        {/* Étoiles */}
                        <div className="flex mb-4">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className="w-5 h-5 text-yellow-400 fill-current"
                              viewBox="0 0 20 20"
                              xmlns="http://www.w3.org/2000/svg">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>

                        {/* Message */}
                        <blockquote className="text-gray-700 mb-4 italic flex-grow">
                          &ldquo;
                          {recommandation.metadata?.message ||
                            recommandation.title}
                          &rdquo;
                        </blockquote>

                        {/* Informations client */}
                        <div className="border-t pt-4 mt-auto">
                          <div className="flex items-center justify-between">
                            <div>
                              {recommandation.metadata?.societe && (
                                <p className="font-semibold text-gray-800">
                                  {recommandation.metadata.societe}
                                </p>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDateLong(
                                recommandation.metadata.date_de_publication
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>

          {/* Indicateurs de pagination */}
          {recommandations.length > visibleItems && (
            <div className="flex justify-center mt-8 space-x-2">
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                    index === currentIndex
                      ? "bg-blue-600"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Aller à la position ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Recommandations;
